import { Link } from "react-router-dom";

/* The questions people ask right before paying, on the page most of them
   land on.

   The homepage answered what Veyra does and never what buying it involves --
   how to pay, whether it renews, whether it works on a second PC, what
   happens if it does not run. Those answers lived on product pages and in the
   Terms, one click and a scroll away from someone deciding.

   Every answer restates something already stated elsewhere, often in the
   same words: data/productFaq.ts (install, one machine, refunds, support),
   public/llms.txt (card only, redeem keys), and the checkout code (a
   one-time Stripe payment, subscriptions stored with autoRenew false).
   Nothing new is promised here.

   <details> rather than a scripted accordion: it opens without JavaScript,
   the answers are in the page for anything that reads it, and the browser
   handles keyboard and screen-reader behaviour. */

const DISCORD_URL = "https://discord.gg/d64RvvK9Ta";

const linkClass = "font-medium text-primary-light underline decoration-primary-light/30 underline-offset-2 hover:text-white";

const FAQ: { q: string; a: React.ReactNode; plainA?: string }[] = [
  {
    q: "What games do you have cheats for?",
    a: (
      <>
        Deadlock, CS2, FiveM and Roblox, with Apex Legends and Rust in development. Whether each can be bought right
        now is on the <Link to="/status" className={linkClass}>cheat status page</Link>. Every Deadlock
        purchase includes both the Internal and External build with aimbot, ESP wallhack, glow, soul aim and hero
        scripts. See the{" "}
        <Link to="/products" className={linkClass}>
          products page
        </Link>{" "}
        for the full list.
      </>
    ),
    plainA:
      "Deadlock, CS2, FiveM and Roblox, with Apex Legends and Rust in development. Whether each can be bought right now is on the cheat status page. Every Deadlock purchase includes both the Internal and External build with aimbot, ESP wallhack, glow, soul aim and hero scripts.",
  },
  {
    q: "Do you have a Deadlock aimbot and ESP?",
    a: (
      <>
        Yes. The Deadlock cheat includes a full aimbot with projectile prediction, silent aim, smoothing and bone
        selection, plus a complete ESP wallhack with boxes, skeletons, glow, health bars, radar and off-screen
        indicators. Read the{" "}
        <Link to="/deadlock-cheats" className={linkClass}>
          Deadlock cheats guide
        </Link>{" "}
        for every feature in detail.
      </>
    ),
    plainA:
      "Yes. The Deadlock cheat includes a full aimbot with projectile prediction, silent aim, smoothing and bone selection, plus a complete ESP wallhack with boxes, skeletons, glow, health bars, radar and off-screen indicators.",
  },
  {
    q: "Is there a free Deadlock hack?",
    a: (
      <>
        Yes. A free Veyra build is available in our{" "}
        <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
          Discord
        </a>
        . It is the same team that maintains the paid build, so you can try the real software before deciding to buy.{" "}
        <Link to="/free-deadlock-cheat" className={linkClass}>
          How to get the free version
        </Link>
        .
      </>
    ),
    plainA:
      "Yes. A free Veyra build is available in the Veyra Discord. It is the same team that maintains the paid build, so you can try the real software before deciding to buy.",
  },
  {
    q: "How fast do I get access?",
    a: (
      <>
        The moment your payment clears, with no manual step. Open the{" "}
        <Link to="/downloads" className={linkClass}>
          Downloads
        </Link>{" "}
        page and run the Veyra loader: it signs you in, fetches the current build for what you own and starts it.
        Your account needs a verified email address first, which is the link sent to you when you sign up.
      </>
    ),
    plainA:
      "The moment your payment clears, with no manual step. Open the Downloads page and run the Veyra loader: it signs you in, fetches the current build for what you own and starts it.",
  },
  {
    q: "Is it a subscription?",
    a: "No. Each plan is one card payment for a week or a month. Nothing renews on its own, so there is nothing to cancel. When it runs out, buy again if you want more.",
    plainA:
      "No. Each plan is one card payment for a week or a month. Nothing renews on its own, so there is nothing to cancel. When it runs out, buy again if you want more.",
  },
  {
    q: "How do I pay?",
    a: (
      <>
        By card, at checkout on the game's page. Card is the only payment method: crypto, PayPal and Paysafecard are
        not accepted. Coupon codes go in at checkout, and a key can be used on the{" "}
        <Link to="/redeem" className={linkClass}>
          Redeem
        </Link>{" "}
        page instead of paying.
      </>
    ),
    plainA:
      "By card, at checkout on the game's page. Card is the only payment method: crypto, PayPal and Paysafecard are not accepted.",
  },
  {
    q: "Can I use it on more than one computer?",
    a: "A licence is tied to one machine. The hardware identifier of the computer you activate on is recorded so a single licence cannot be shared between people. If your hardware changes, ask support to reset it.",
    plainA:
      "A licence is tied to one machine. The hardware identifier of the computer you activate on is recorded so a single licence cannot be shared between people. If your hardware changes, ask support to reset it.",
  },
  {
    q: "Can I get a refund?",
    a: (
      <>
        Purchases are final, with one exception: if your hardware cannot run the software at all, you are eligible
        for a refund. Contact support before doing anything else. Chargebacks are prohibited and result in a
        permanent ban. The full terms are on the{" "}
        <Link to="/terms" className={linkClass}>
          Terms
        </Link>{" "}
        page.
      </>
    ),
    plainA:
      "Purchases are final, with one exception: if your hardware cannot run the software at all, you are eligible for a refund. Contact support before doing anything else.",
  },
  {
    q: "Where do I get help?",
    a: (
      <>
        On our{" "}
        <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
          Discord
        </a>
        . That is where support actually happens, and it is the fastest way to reach somebody.
      </>
    ),
    plainA:
      "On our Discord. That is where support actually happens, and it is the fastest way to reach somebody.",
  },
];

export const HOME_FAQ_PLAIN = FAQ.map((f) => ({
  q: f.q,
  a: f.plainA ?? (typeof f.a === "string" ? f.a : ""),
})).filter((f) => f.a);

export default function HomeFaq() {
  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)] lg:gap-14">
      {/* Pinned on lg, so opening answers does not leave it stranded at the
          top of an empty column. */}
      <div className="lg:sticky lg:top-24 lg:self-start">
        <h2 className="display text-5xl text-white md:text-6xl">
          Before
          <br />
          <span className="text-primary-light">you buy.</span>
        </h2>
        <p className="mt-4 max-w-sm text-white/60">
          The questions people ask right before paying. Anything else, ask us on{" "}
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
            Discord
          </a>
          .
        </p>
      </div>

      <div className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
        {FAQ.map((item, i) => (
          <details key={item.q} className="group" open={i === 0}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-left font-semibold text-white transition-colors hover:text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light [&::-webkit-details-marker]:hidden">
              {item.q}
              <span
                aria-hidden
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-white/15 text-primary-light transition-transform duration-300 group-open:rotate-45 motion-reduce:transition-none"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeWidth={2.4} d="M12 5v14M5 12h14" />
                </svg>
              </span>
            </summary>
            <p className="max-w-2xl pb-6 pr-12 text-sm leading-relaxed text-white/65">{item.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}
